import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import LOGO from "../../../assets/img/MOSIDA-logo.png";
import FifaBg from "../../../assets/img/fifa/fifa-inner.png";
import BackArrow from "../../../compoment/icon/BackArrow";
import type { RootState } from "../../../redux/store";
import {
  isFifaWorldCup2026Station,
  clearFifaPlayerProfile,
  clearFifaPlayerStats,
  persistFifaPlayerProfile,
  readActiveStoredStation,
  readFifaPlayerProfile,
  resolveFifaCapturedImage,
  resolveFifaStationSlug,
  type FifaPlayerProfile,
} from "../../../utils/stationFlowStorage";
import { readSelectedAiSelfieArtFromStorage } from "../../../services/stylePromptResolver";
import { readSelectedFifaCategory } from "../../../services/fifaCategoryService";
import {
  getFifaTeams,
  persistSelectedFifaTeam,
  type FifaTeam,
} from "../../../services/fifaTeamService";
import Loader from "../../../compoment/ui/Loader";
import FifaThemeDropdown from "../../../compoment/ui/FifaThemeDropdown";
import { allowOnlyOtpDigits } from "../../../modal/auth/phoneUtils";

const EMPTY_PROFILE: FifaPlayerProfile = {
  clubTeam: "",
  firstName: "",
  lastName: "",
  jerseyNumber: "",
};

const EnterProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const uploadedImageUrl = useSelector(
    (state: RootState) => state.uploadFlow.uploadedImageUrl
  );

  const stationData = useMemo(() => readActiveStoredStation(), [location.state]);
  const selectedAiSelfieArt = useMemo(
    () => readSelectedAiSelfieArtFromStorage(),
    [location.state]
  );
  const stationSlug = useMemo(
    () => resolveFifaStationSlug(stationData, selectedAiSelfieArt),
    [stationData, selectedAiSelfieArt]
  );
  const isFifaFlow = isFifaWorldCup2026Station(stationData, selectedAiSelfieArt);
  const locationCapturedImage = (location.state as { capturedImage?: string } | null)
    ?.capturedImage;
  const capturedImage = resolveFifaCapturedImage({
    locationImage: locationCapturedImage,
    reduxImage: uploadedImageUrl,
  });

  const [profile, setProfile] = useState<FifaPlayerProfile>(() => ({
    ...EMPTY_PROFILE,
    ...readFifaPlayerProfile(),
  }));
  const [fifaTeams, setFifaTeams] = useState<FifaTeam[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState<string | null>(null);

  const teamOptions = useMemo(
    () =>
      [...fifaTeams].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [fifaTeams]
  );

  const teamDropdownOptions = useMemo(
    () => teamOptions.map((team) => ({ value: team.name, label: team.name })),
    [teamOptions]
  );

  useEffect(() => {
    if (!isFifaFlow) return;

    let cancelled = false;

    (async () => {
      setTeamsLoading(true);
      setTeamsError(null);

      const res = await getFifaTeams();
      if (cancelled) return;

      if (!res.success && res.data.length === 0) {
        setTeamsError(res.error || res.message || "Could not load teams");
        setFifaTeams([]);
      } else {
        setFifaTeams(res.data);
      }

      setTeamsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isFifaFlow]);

  useEffect(() => {
    if (!profile.clubTeam.trim()) return;

    const matchedTeam = teamOptions.find(
      (team) => team.name.trim().toLowerCase() === profile.clubTeam.trim().toLowerCase()
    );
    if (matchedTeam) {
      persistSelectedFifaTeam(matchedTeam);
    }
  }, [profile.clubTeam, teamOptions]);

  useEffect(() => {
    if (!isFifaFlow) {
      navigate(`/${stationSlug}/choose-option`, { replace: true });
      return;
    }

    if (!capturedImage) {
      navigate(`/${stationSlug}/upload-fifa-image`, { replace: true });
    }
  }, [capturedImage, isFifaFlow, navigate, stationSlug]);

  const updateField = (field: keyof FifaPlayerProfile, value: string) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleTeamChange = (teamName: string) => {
    updateField("clubTeam", teamName);

    const selectedTeam = teamOptions.find((team) => team.name === teamName);
    if (selectedTeam) {
      persistSelectedFifaTeam(selectedTeam);
    }
  };

  const isFormComplete =
    profile.clubTeam.trim() &&
    profile.firstName.trim() &&
    profile.lastName.trim() &&
    profile.jerseyNumber.trim();

  const handleProceed = () => {
    if (!isFormComplete || !capturedImage) return;

    persistFifaPlayerProfile(profile);

    const routeState = {
      capturedImage,
      stationData,
      fifaPlayerProfile: profile,
    };

    const selectedCategory = readSelectedFifaCategory();
    if (selectedCategory?.is_stats === false) {
      clearFifaPlayerStats();
      navigate(`/${stationSlug}/creating-fifa-card`, {
        state: {
          ...routeState,
          fifaStatsSource: "ai",
          fifaStatsSkipped: true,
        },
      });
      return;
    }

    navigate(`/${stationSlug}/fifa-stats-choice`, {
      state: routeState,
    });
  };

  const handleBack = () => {
    clearFifaPlayerProfile();
    clearFifaPlayerStats();
    setProfile({ ...EMPTY_PROFILE });
    navigate(`/${stationSlug}/upload-fifa-image`, {
      state: { capturedImage, stationData },
    });
  };

  if (!isFifaFlow) {
    return null;
  }



  return (
    <>
      {teamsLoading &&
        <Loader />
      }
      <section
        className="enterProfileSection"
        style={{ backgroundImage: `url(${FifaBg})` }}
      >
        <div className="enterProfileInner">
          <div className="mosidaLogoWhite enterProfileLogo mb-5">
            <img src={LOGO} alt="MOSIDA logo" className="logo" />
            <span className="badgeCreative">FIFA World Cup</span>
          </div>

          <button
            type="button"
            className="enterProfileBack"
            onClick={handleBack}
          >
            <span className="me-1">
              <BackArrow />
            </span>
            Back
          </button>

          <div className="enterProfileBody">
            <h1 className="enterProfileHeading mb-5 pb-5">
              <div className="enterProfileHeadingLine">
                ENTER{" "}
                <span className="enterProfileHeadingLine--gold">YOUR PROFILE</span>
              </div>
            </h1>

            <form
              className="enterProfileForm"
              onSubmit={(event) => {
                event.preventDefault();
                handleProceed();
              }}
            >
              <label className="enterProfileField enterProfileField--full">
                <span className="enterProfileLabel">CLUB / TEAM</span>
                {teamsError && (
                  <span className="enterProfileTeamsError">{teamsError}</span>
                )}
                {!teamsError && teamOptions.length === 0 && (
                  <span className="enterProfileTeamsError">No teams available.</span>
                )}
                <FifaThemeDropdown
                  value={profile.clubTeam}
                  onChange={handleTeamChange}
                  options={teamDropdownOptions}
                  placeholder="SELECT YOUR TEAM/CLUB"
                  disabled={teamOptions.length === 0}
                  ariaLabel="Club or team"
                />
              </label>

              <div className="enterProfileRow">
                <label className="enterProfileField">
                  <span className="enterProfileLabel">FIRST NAME</span>
                  <input
                    type="text"
                    className="enterProfileInput"
                    placeholder="ENTER YOUR FIRST NAME"
                    value={profile.firstName}
                    onChange={(event) => updateField("firstName", event.target.value)}
                  />
                </label>
                <label className="enterProfileField">
                  <span className="enterProfileLabel">LAST NAME</span>
                  <input
                    type="text"
                    className="enterProfileInput"
                    placeholder="ENTER YOUR LAST NAME"
                    value={profile.lastName}
                    onChange={(event) => updateField("lastName", event.target.value)}
                  />
                </label>
                <label className="enterProfileField">
                  <span className="enterProfileLabel">JERSEY #</span>
                  <input
                    type="text"
                    className="enterProfileInput"
                    placeholder="ENTER JERSEY #"
                    value={profile.jerseyNumber}
                    onKeyDown={(event) => { allowOnlyOtpDigits(event.key); }}
                    onChange={(event) => updateField("jerseyNumber", allowOnlyOtpDigits(event.target.value))}
                  />
                </label>
              </div>

              <button
                type="submit"
                className="enterProfileProceedBtn mt-5"
                disabled={!isFormComplete}
              >
                PROCEED
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default EnterProfile;